import { useCallback, useMemo, useRef } from 'react';
import { getAvatarImage } from 'app/utils/utils';
import { useInfiniteLoader, useResource } from 'app/utils/hooks';
import { fetchCountriesEffect } from 'app/store/Countries/effects';
import { getCountries } from 'app/store/Countries/selectors';
import { fetchGlobalSearchEffect } from 'app/store/GlobalSearch/effects';
import { useAppDispatch } from 'app/store';
import { ProfileCard } from 'types/profile';
import { globalSearchScrollContainerId } from 'app/components/GlobalSearch/constants';
import { getNextSearchLink } from 'app/store/GlobalSearch/selectors';
import { Box, BoxProps, Flex, Text } from '@chakra-ui/react';
import { ChakraAvatar } from 'app/components/Core';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { Row } from '@tanstack/react-table';
import {
  setSearchAction,
  setSearchByCollaboratorAction,
} from 'app/store/GlobalSearch/slice';
import { searchCollaboratorSelectAction } from 'app/store/Mixpanel/slice';
import { ProfileImageSize } from 'types/enums';
import { getColumns } from './utils';
import { CollaboratorResultsRow } from './CollaboratorResultsRow';

interface CollaboratorResultsProps extends BoxProps {
  collaborators: ProfileCard[];
  topResults?: boolean;
}

export const CollaboratorResults = ({
  collaborators,
  topResults,
  ...boxProps
}: CollaboratorResultsProps) => {
  const dispatch = useAppDispatch();
  const [countriesDDL] = useResource(fetchCountriesEffect, getCountries);

  const [, infiniteScrollProps, loadingMore] = useInfiniteLoader({
    listId: globalSearchScrollContainerId,
    dataLength: collaborators.length,
    nextLinkSelector: getNextSearchLink,
    fetchNext: (next) => fetchGlobalSearchEffect({ next }),
  });

  const onSearchByCollaborator = useCallback(
    async (collaboratorDetails: ProfileCard) => {
      if (!collaboratorDetails) {
        return;
      }

      await dispatch(setSearchAction(''));
      await dispatch(setSearchByCollaboratorAction(collaboratorDetails));
      dispatch(searchCollaboratorSelectAction(collaboratorDetails.subject));
      dispatch(fetchGlobalSearchEffect({ newSearch: true }));
    },
    [dispatch]
  );

  const tableKey = 'global-search';
  const ref = useRef<HTMLDivElement>(null);
  const totalWidth = useTotalWidth(ref);
  const columns = useMemo(() => getColumns({ totalWidth }), [totalWidth]);

  const rowRenderer = useCallback(
    (item: Row<ProfileCard>) => {
      const collaborator = item.original;

      return (
        <CollaboratorResultsRow
          key={collaborator.guid}
          collaborator={collaborator}
          countriesDDL={countriesDDL || {}}
          onSearchByCollaborator={onSearchByCollaborator}
        />
      );
    },
    [countriesDDL, onSearchByCollaborator]
  );

  return (
    <Box {...boxProps}>
      <Text fontSize="md" fontWeight={500} pb={4}>
        Collaborators
      </Text>
      {topResults ? (
        <Flex gap={2} overflowX="auto" overflowY="hidden">
          {collaborators.map((collaborator) => (
            <Box
              key={collaborator.guid}
              p={2}
              cursor="pointer"
              _hover={{
                bg: 'M.20',
                rounded: 6,
                transform: 'scale(1.02)',
              }}
              _active={{
                transform: 'scale(0.97)',
              }}
              onClick={() => onSearchByCollaborator(collaborator)}
            >
              <ChakraAvatar
                size="3xl"
                src={getAvatarImage(
                  collaborator?.profileImages,
                  ProfileImageSize.AVATAR_SMALL
                )}
                initials={collaborator?.initials}
                colors={collaborator?.avatarColors}
              />
              <Text mt={2}>{collaborator.displayName}</Text>
              <Text fontSize="xs" color="textSubdued">
                {collaborator.city}
              </Text>
            </Box>
          ))}
        </Flex>
      ) : (
        <StickyTable<ProfileCard>
          data-testid="global-search-collaborators-list"
          tableKey={tableKey}
          ref={ref}
          data={collaborators}
          columns={columns}
          rowRenderer={rowRenderer}
          infiniteScrollProps={infiniteScrollProps}
          loadMore={loadingMore}
          showHeader={false}
        />
      )}
    </Box>
  );
};
